import { FC, KeyboardEvent } from 'react'
import { faArrowsUpDown, faArrowUpRightAndArrowDownLeftFromCenter } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, styled } from '@mui/material'
import { blueGrey } from '@mui/material/colors'

const RESIZE_STEP = 10

type Props = {
  onResize?: (delta: number) => void
  value: number
  min: number
  max: number
  label: string
}

export const ResizableArea: FC<Props> = ({ onResize, value, min, max, label }) => {
  // a11y: VoiceOver won't reliably speak aria-valuenow on a custom slider; valuetext is read verbatim.
  const valueText = `${Math.round(value)} pixels height`

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      onResize?.(RESIZE_STEP)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      onResize?.(-RESIZE_STEP)
    }
  }

  return (
    <Box>
      <ResizingControlsContainer>
        <VerticalAxisResizeContainer
          // a11y: slider so the height is announced with a value; arrow keys resize.
          role='slider'
          aria-label={label}
          aria-orientation='vertical'
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-valuetext={valueText}
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <VerticalAxisResizeIcon icon={faArrowsUpDown} />
        </VerticalAxisResizeContainer>
        <RotatedBothAxisIcon icon={faArrowUpRightAndArrowDownLeftFromCenter} />
      </ResizingControlsContainer>
    </Box>
  )
}

const ResizingControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  color: blueGrey[500],
  backgroundColor: blueGrey[100],
  padding: '3px',
}))

const VerticalAxisResizeContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexGrow: 1,
  cursor: 'ns-resize',
}))

const RotatedBothAxisIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  transform: 'rotate(90deg)',
  fontSize: theme.typography.body2.fontSize,
}))

const VerticalAxisResizeIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  marginLeft: '14px',
  fontSize: theme.typography.body2.fontSize,
}))
